import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { KeyManagementService } from '@/services/KeyManagementService';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { EncryptionService } from '@/services/encryption/EncryptionService';
import { ICursorElements } from '@/store/types/search.types';

import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { store } from '@/store/configuration';
import {
  ISupportUser,
  ISupportUserFilter,
  ISupportUserRequest,
  supportUserLockReason,
  supportUserStatus,
  TFavorite,
} from '@/features/dashboard-users/store/support-user.model';
import { fetchMethods } from '@/types/FetchService.types';
import { IChangePassword } from '@/features/dashboard-users/store/types/password.types';
import { filtersToQueryParameters } from '@/utils/filtersToQueryParameters';

export class SupportUserService {
  static queryKey = 'Support Users';
  static getSupportUsers = async (filter?: ISupportUserFilter): Promise<ICursorElements<ISupportUser>> => {
    try {
      const config = getStaticDashboardConfig();
      const params = filtersToQueryParameters(filter);

      const supportUsersResponse = await fetchQuery(
        [
          this.queryKey,
          {
            action: 'support-user',
            method: fetchMethods.GET,
            params: Object.fromEntries(params),
          },
        ],
        `${config.api.baseUrl}/core-connect/support-user?${params.toString()}`,
        {
          headers: createHttpHeader(),
          staleTime: 0,
          cache: 'no-cache',
        },
      );

      return await decryptTreezorResponse<ISupportUser>(supportUsersResponse, {
        shouldSkipEncryption: true,
        hasCursor: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getSupportUserMetadata = async (supportUserId: string): Promise<any> => {
    try {
      const config = getStaticDashboardConfig();

      const supportUserResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, id: supportUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${supportUserId}`,
        {
          headers: createHttpHeader(),
        },
      );

      const SupportUser = await decryptTreezorResponse<ISupportUser>(supportUserResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });

      return SupportUser?.metadata || {};
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateCurrentSupportUserFavorites = async (
    supportUserId: string,
    favorite: TFavorite,
  ): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();

      const previousMetadata = await this.getSupportUserMetadata(supportUserId);
      const currentFavorites: TFavorite[] = previousMetadata?.favorites || [];

      const isStarred = currentFavorites?.some((item) => {
        if (item.type === favorite.type) {
          if (item.type === 'wallet') return item.walletId === favorite.walletId;
        }
        return false;
      });

      const newFavorites: TFavorite[] = isStarred
        ? currentFavorites?.filter((item) => {
            if (item.type === favorite.type) {
              if (item.type === 'wallet') return item.walletId !== favorite.walletId;
            }
            return false;
          })
        : [...currentFavorites, favorite];

      const supportUserMetadataResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: supportUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${supportUserId}/metadata`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ metadata: { ...previousMetadata, favorites: newFavorites } }),
        },
      );

      return await decryptTreezorResponse<ISupportUser>(supportUserMetadataResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateSupportUserMetadata = async (supportUserId: string, data: any): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();

      const previousMetadata = await this.getSupportUserMetadata(supportUserId);

      const supportUserMetadataResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: supportUserId, params: { data } }],
        `${config.api.baseUrl}/core-connect/support-user/${supportUserId}/metadata`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ metadata: { ...previousMetadata, ...data } }),
        },
      );

      return await decryptTreezorResponse<ISupportUser>(supportUserMetadataResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateCurrentSupportUserMetadata = async (data: any): Promise<ISupportUser | undefined> => {
    const state = store.getState();
    const currentSupportUser: ISupportUser = state.auth?.currentSupportUser?.state;
    if (currentSupportUser) {
      await this.updateSupportUserMetadata(currentSupportUser.id, data);
    }
    return currentSupportUser;
  };

  static searchSupportUser = async (supportUserId: string): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();

      const userRole = store.getState().accessControl?.role?.value?.slug || 'CONFIG';

      const supportUsersResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, user: supportUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${supportUserId}`,
        {
          headers: createHttpHeader(),
          userRole,
        },
      );

      return await decryptTreezorResponse<ISupportUser>(supportUsersResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static addSupportUser = async (supportUser: ISupportUserRequest): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();
      const { username, password, samlKey, scopes } = supportUser;

      const body: any = { username, password, scopes, ...(samlKey ? { samlKey } : {}) };
      if (config.settings.dashboardUsers?.accessExpiration) {
        body.accessExpiration = config.settings.dashboardUsers.accessExpiration;
      }

      const supportUsersResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, id: supportUser.id, params: supportUser }],
        `${config.api.baseUrl}/core-connect/support-user`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(body),
        },
      );

      const newSupportUser = await decryptTreezorResponse<ISupportUser>(supportUsersResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });

      if (newSupportUser && supportUser.passphrase) {
        const cryptoService = await EncryptionService.getInstance();
        const keys = await cryptoService.generateSupportUserKeys(supportUser.passphrase);
        await KeyManagementService.setSupportUserKeys(newSupportUser.id, keys);
      }

      return newSupportUser;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static updateSupportUser = async (
    supportUser: ISupportUserRequest,
    shouldRestore?: boolean,
  ): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();
      const { id, username, passphrase, password, samlKey, scopes } = supportUser;

      if (shouldRestore) {
        await this.restoreSupportUser(id);
      }

      const supportUsersResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.PUT, user: id, params: { username, passphrase, password, samlKey, scopes } },
        ],
        `${config.api.baseUrl}/core-connect/support-user/${id}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ username, password, passphrase, scopes, ...(samlKey ? { samlKey } : {}) }),
        },
      );

      const updatedSupportUser = await decryptTreezorResponse<ISupportUser>(supportUsersResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
      if (updatedSupportUser && supportUser.passphrase) {
        const cryptoService = await EncryptionService.getInstance();
        const keys = await cryptoService.generateSupportUserKeys(supportUser.passphrase);
        await KeyManagementService.setSupportUserKeys(updatedSupportUser.id, keys);
      }
      return updatedSupportUser;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static removeSupportUser = async (supportUser: ISupportUser): Promise<string> => {
    try {
      const config = getStaticDashboardConfig();
      const { id } = supportUser;

      if (
        supportUser.metadata &&
        supportUser.status !== supportUserStatus.LOCK &&
        supportUser.lockReason !== supportUserLockReason.TEMPORARY_BLOCK
      ) {
        await fetchQuery(
          [this.queryKey, { method: fetchMethods.PUT, id }],
          `${config.api.baseUrl}/core-connect/support-user/${id}/metadata`,
          {
            headers: createHttpHeader(),
            body: JSON.stringify({ metadata: { role: {} } }),
          },
        );
      }

      await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: id }],
        `${config.api.baseUrl}/core-connect/support-user/${id}/lockUnlock`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            status: supportUserStatus.LOCK,
            lockReason: supportUserLockReason.DELETED,
          }),
        },
      );

      return id;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static restoreSupportUser = async (supportUserId: string): Promise<string> => {
    try {
      const config = getStaticDashboardConfig();

      await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: supportUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${supportUserId}/lockUnlock`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            status: supportUserStatus.UNLOCK,
          }),
        },
      );

      await this.updateAccessExpirationDate(supportUserId);

      return supportUserId;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static unblockSupportUser = async (supportUser: ISupportUser): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();
      const { id } = supportUser;

      await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: id }],
        `${config.api.baseUrl}/core-connect/support-user/${id}/unblock`,
        {
          headers: createHttpHeader(),
        },
      );
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static suspendSupportUser = async (supportUser: ISupportUser): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();
      const { id } = supportUser;

      const payload = {
        status: supportUserStatus.LOCK,
        lockReason: supportUserLockReason.TEMPORARY_BLOCK,
      };

      const supportUserResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: id }],
        `${config.api.baseUrl}/core-connect/support-user/${id}/lockUnlock`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(payload),
        },
      );
      return await decryptTreezorResponse<ISupportUser>(supportUserResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static restoreSupportUserAccess = async (supportUser: ISupportUser): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();
      const { id } = supportUser;

      const payload = {
        status: 'UNLOCK',
      };

      const supportUserResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, user: id }],
        `${config.api.baseUrl}/core-connect/support-user/${id}/lockUnlock`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(payload),
        },
      );
      return await decryptTreezorResponse<ISupportUser>(supportUserResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static changePassword = async (dashboardUserId: string, payload: IChangePassword): Promise<ISupportUser> => {
    try {
      const config = getStaticDashboardConfig();

      const supportUserResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: dashboardUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${dashboardUserId}/changePassword`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(payload),
        },
      );
      return await decryptTreezorResponse<ISupportUser>(supportUserResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateAccessExpirationDate = async (dashboardUserId: string) => {
    try {
      const config = getStaticDashboardConfig();

      const supportUserResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: dashboardUserId }],
        `${config.api.baseUrl}/core-connect/support-user/${dashboardUserId}/accessExpiration`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            accessExpiration: config.settings.dashboardUsers?.accessExpiration,
          }),
        },
      );
      return await decryptTreezorResponse<ISupportUser>(supportUserResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
export default SupportUserService;
